import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
    console.error('[ErrorBoundary] Error:', error);
    this.setState({ componentStack: info.componentStack || null });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  handleCopy = () => {
    const text = [
      this.state.error?.message || '',
      this.state.componentStack || '',
    ].join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card className="shadow-xs">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <AlertTriangle size={28} className="text-amber-500" />
            <div>
              <p className="text-sm font-medium text-foreground">
                {this.props.fallbackMessage || 'Something went wrong'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
            {this.state.componentStack && (
              <pre className="text-[10px] text-left text-muted-foreground bg-muted rounded p-3 max-w-full overflow-auto max-h-[200px] w-full mt-2">
                {this.state.componentStack}
              </pre>
            )}
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={this.handleRetry}>
                <RefreshCw size={14} /> Try again
              </Button>
              {this.state.componentStack && (
                <Button variant="ghost" size="sm" className="gap-2" onClick={this.handleCopy}>
                  <Copy size={14} /> Copy error
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
